import { dialogClasses } from '@mui/material/Dialog';
import { type Theme, type Components } from '@mui/material/styles';

import { IconX } from '@/components/icon/tabler/IconX';
import { IconCircleCheck as CheckCircleIcon } from '@/components/icon/tabler/IconCircleCheck';
import { IconAlertCircle as ErrorIcon } from '@/components/icon/tabler/IconAlertCircle';
import { IconInfoCircle as InfoIcon } from '@/components/icon/tabler/IconInfoCircle';
import { IconAlertTriangle as WarningIcon } from '@/components/icon/tabler/IconAlertTriangle';
import { signalIndeterminate } from '@/components/Loaders/utils';
import {
  controlTokens,
  DIALOG_CRISTA_CORNER_CLASS,
  gray,
  green,
  loadingTokens,
  orange,
  red,
  revealTokens,
  surfaceTokens,
  typography,
} from '@/theme/themePrimitives';

/**
 * Signal 06d (16 out, compacted 17 out): one alert, drawn like the plan card's "Pagamento não
 * confirmado" box. A light tint, a thin rule in the same tone, a bare icon and the text in the
 * tone's ink. It sits at the size of the content around it and never casts a shadow.
 */
const alertTone = {
  info: { soft: '#EEF4F9', border: '#CFE0EC', icon: controlTokens.info, ink: controlTokens.info },
  success: { soft: green[50], border: green[200], icon: green[400], ink: green[600] },
  warning: { soft: orange[50], border: orange[200], icon: orange[500], ink: orange[600] },
  error: { soft: red[50], border: red[200], icon: red[400], ink: red[600] },
} as const;

const alertIconMapping = {
  success: <CheckCircleIcon fontSize="inherit" />,
  error: <ErrorIcon fontSize="inherit" />,
  warning: <WarningIcon fontSize="inherit" />,
  info: <InfoIcon fontSize="inherit" />,
};

export const feedbackCustomizations: Components<Theme> = {
  MuiAlert: {
    defaultProps: { slots: { closeIcon: IconX }, iconMapping: alertIconMapping },
    styleOverrides: {
      root: ({ theme }) => ({
        // A title (or an AlertFooter) makes the card; a message alone is the compact line.
        border: '1px solid transparent',
        borderRadius: 12,
        padding: '12px 14px',
        alignItems: 'flex-start',
        // An Alert is a Paper: without this it inherits the card shadow and floats over the page.
        boxShadow: 'none',
        '& .MuiIconButton-root': {
          border: 0,
          width: 24,
          height: 24,
          margin: '-3px -6px 0 0',
          color: 'inherit',
          opacity: 0.8,
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: 'rgba(17, 0, 86, 0.06)', opacity: 1 },
        },
        '& .MuiAlert-icon': {
          padding: 0,
          margin: '1px 10px 0 0',
          display: 'flex',
          flexShrink: 0,
          fontSize: 18,
          opacity: 1,
        },
        '& .MuiAlert-message': {
          flex: 1,
          minWidth: 0,
          padding: 0,
          fontSize: '13px',
          lineHeight: 1.45,
          color: 'inherit',
          overflow: 'visible',
        },
        '& .MuiAlertTitle-root': {
          fontFamily: theme.typography.subtitle2.fontFamily,
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: 1.4,
          marginTop: 0,
          marginBottom: '2px',
          color: 'inherit',
        },
        // No `:only-child` tricks: CSS skips text nodes, so a title followed by plain text counted as
        // alone and centred the icon away from the first line.
        '& a:not(.MuiButton-root)': {
          color: 'inherit',
          fontWeight: 600,
          textDecoration: 'underline',
          textUnderlineOffset: 3,
        },
        '& .MuiAlert-action': {
          padding: 0,
          margin: '0 0 0 12px',
          alignSelf: 'center',
          '& .MuiButton-text': {
            color: 'inherit',
            fontWeight: 600,
            fontSize: '13px',
            textDecoration: 'underline',
            textUnderlineOffset: 3,
          },
        },
        // Compact: one line, no title and no footer.
        '&:not(:has(.MuiAlertTitle-root, .AlertFooter-root))': {
          borderRadius: 10,
          padding: '8px 12px',
          alignItems: 'center',
          '& .MuiAlert-icon': {
            margin: '0 8px 0 0',
            fontSize: 16,
          },
          '& .MuiAlert-message': {
            fontWeight: 500,
            lineHeight: 1.4,
          },
          // The link at the end of the line stays a link: a 32px button made the line 54px tall.
          '& .MuiAlert-action': {
            marginRight: 0,
            '& .MuiButton-root': { minHeight: 0, height: 'auto', padding: '2px 4px' },
          },
          '& .MuiIconButton-root': { margin: '-3px -6px -3px 0' },
        },
        variants: [
          {
            props: (props) => Boolean(props.onClick),
            style: {
              cursor: 'pointer',
              // The whole alert opens something: a chevron at its end says so (Signal 06d).
              '&::after': {
                content: '""',
                width: 16,
                height: 16,
                flexShrink: 0,
                alignSelf: 'center',
                marginLeft: 8,
                backgroundColor: 'currentColor',
                mask: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M9 6l6 6-6 6'/%3E%3C/svg%3E") center / contain no-repeat`,
              },
              '&:hover': { filter: 'brightness(0.98)' },
              '&:focus-visible': {
                outline: `2px solid ${controlTokens.cobalt}`,
                outlineOffset: 2,
              },
            },
          },
          ...(['info', 'success', 'warning', 'error'] as const).map((severity) => ({
            props: { severity },
            style: {
              backgroundColor: alertTone[severity].soft,
              borderColor: alertTone[severity].border,
              color: alertTone[severity].ink,
              '& .MuiAlert-icon': { color: alertTone[severity].icon },
            },
          })),
        ],
      }),
    },
  },
  // Product modal (prancha 13): r20, widths 420 / 560 / 760, header 20/22, foot under an n-150 rule.
  // The sheet on a phone keeps the r28 open edge from prancha 07 (`Modal` overrides it).
  MuiDialog: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiBackdrop-root': { backgroundColor: surfaceTokens.scrim },
        '& .MuiDialog-paper': {
          borderRadius: revealTokens.modalRadius,
          padding: 0,
          border: 0,
          backgroundColor: (theme.vars || theme).palette.background.paper,
          boxShadow: surfaceTokens.shadowOverlay,
          color: theme.palette.text.primary,
          ...theme.applyStyles('dark', { boxShadow: 'none' }),
          [theme.breakpoints.down('sm')]: {
            borderRadius: 0,
          },
          [`&.${dialogClasses.paperWidthXs}`]: { maxWidth: revealTokens.modalWidthXs },
          [`&.${dialogClasses.paperWidthSm}`]: { maxWidth: revealTokens.modalWidthSm },
          [`&.${dialogClasses.paperWidthMd}`]: { maxWidth: revealTokens.modalWidthMd },
          [`&.${DIALOG_CRISTA_CORNER_CLASS}`]: { borderTopRightRadius: 64 },
        },
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily: typography.h1.fontFamily,
        fontSize: '20px !important',
        fontWeight: 600,
        lineHeight: '22px',
        letterSpacing: '-0.015em',
        color: surfaceTokens.dark,
        // Leaves room for the Modal's absolute close button.
        padding: '20px 56px 12px 22px',
        [theme.breakpoints.down('sm')]: { padding: '20px 52px 12px 20px' },
        ...theme.applyStyles('dark', { color: (theme.vars || theme).palette.text.primary }),
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '8px 22px 20px',
        [theme.breakpoints.down('sm')]: { padding: '8px 20px 20px' },
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        gap: 8,
        padding: '14px 22px',
        [theme.breakpoints.down('sm')]: { padding: '14px 20px' },
        borderTop: `1px solid ${surfaceTokens.section}`,
        '& > :not(style) ~ :not(style)': { marginLeft: 0 },
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      // Signal board 17: ink bar in the product, signal gradient only on `secondary`.
      root: ({ theme }) => ({
        height: 8,
        borderRadius: 8,
        backgroundColor: loadingTokens.track,
        ...theme.applyStyles('dark', {
          backgroundColor: gray[800],
        }),
      }),
      indeterminate: { height: 4 },
      query: { height: 4 },
      bar: { borderRadius: 8 },
      barColorPrimary: { backgroundColor: controlTokens.primary },
      barColorSecondary: { backgroundImage: controlTokens.signalGradient },
      bar1Indeterminate: {
        left: 0,
        right: 'auto',
        width: '32%',
        animation: `${signalIndeterminate} ${loadingTokens.progressIndeterminate}ms ${loadingTokens.progressEasing} infinite`,
        '@media (prefers-reduced-motion: reduce)': {
          animationDuration: `${loadingTokens.progressIndeterminate * 2}ms`,
        },
      },
      bar2Indeterminate: { display: 'none' },
    },
  },
  MuiCircularProgress: {
    defaultProps: {
      size: 24,
    },
    styleOverrides: {
      root: () => ({
        color: controlTokens.cobalt,
      }),
    },
  },
  MuiSkeleton: {
    defaultProps: {
      animation: 'wave',
    },
    styleOverrides: {
      root: {
        backgroundColor: surfaceTokens.section,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          '&::after': { animation: 'none' },
        },
      },
      // Signal board 17: a soft diagonal shine, no opacity pulse.
      wave: {
        '&::after': {
          background: 'linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
          animationDuration: `${loadingTokens.skeletonShimmer}ms`,
          animationDelay: '0s',
        },
      },
    },
  },
  MuiPopper: {
    styleOverrides: {
      root: ({ theme }) => ({
        zIndex: Math.max(...Object.values(theme.zIndex).filter(Boolean)) + 1,
      }),
    },
  },
};
